// frontend/packages/business-console/src/pages/Scheduling/helpers.js
//
// Shared display helpers for the Scheduling tabs: booking-status -> StatusPill
// kind/label, and date/time formatting from stored UTC instants. Kept small
// and copy-clean (no em/en-dashes in any label).

// Booking status enum (backend validation.ts BOOKING_STATUS_VALUES) mapped to
// the shared StatusPill semantic kinds + a human label.
export const BOOKING_STATUS = {
  pending_confirmation: { kind: 'warning', label: 'Pending' },
  confirmed: { kind: 'info', label: 'Confirmed' },
  completed: { kind: 'success', label: 'Completed' },
  cancelled: { kind: 'neutral', label: 'Cancelled' },
  declined: { kind: 'error', label: 'Declined' },
  no_show: { kind: 'error', label: 'No show' },
};

export function statusMeta(status) {
  return BOOKING_STATUS[status] || { kind: 'neutral', label: status || '-' };
}

// A booking is still actionable (can be cancelled/rescheduled/etc.) only while
// it is pending or confirmed. Terminal states hide the row actions.
export function isActiveBooking(status) {
  return status === 'pending_confirmation' || status === 'confirmed';
}

function toDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

// "Mon, Jul 14" in the viewer's local zone. Used for the list day grouping.
export function formatDay(input) {
  const d = toDate(input);
  if (!d) return '-';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// "2:30 PM" in the viewer's local zone.
export function formatTime(input) {
  const d = toDate(input);
  if (!d) return '-';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDateTime(input) {
  const d = toDate(input);
  if (!d) return '-';
  return `${formatDay(input)}, ${formatTime(input)}`;
}

// Group an ordered list of bookings by local calendar day, preserving order.
// Returns [{ dayKey, dayLabel, items }].
export function groupByDay(items) {
  const groups = [];
  const byKey = new Map();
  for (const b of items) {
    const d = toDate(b.startUtc);
    const dayKey = d ? d.toDateString() : 'unknown';
    if (!byKey.has(dayKey)) {
      const group = { dayKey, dayLabel: formatDay(b.startUtc), items: [] };
      byKey.set(dayKey, group);
      groups.push(group);
    }
    byKey.get(dayKey).items.push(b);
  }
  return groups;
}

// Pull a human name for the booker from whatever the booking denormalizes.
// Bookings reference a Customer (D22) and may not embed a name; fall back
// gracefully rather than showing "undefined".
export function bookingCustomerLabel(b) {
  return (
    b.customerName ||
    b.contact?.name ||
    [b.contact?.firstName, b.contact?.lastName].filter(Boolean).join(' ') ||
    b.contact?.email ||
    'Customer'
  );
}

// Convert an <input type="datetime-local"> value (local wall clock, no zone)
// to a UTC ISO string for the API.
export function localInputToUtcIso(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// Convert a stored UTC ISO string to the value an <input type="datetime-local">
// expects (local wall clock, "YYYY-MM-DDTHH:mm"), or '' if absent.
export function utcIsoToLocalInput(iso) {
  const d = toDate(iso);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
